import React, { useState } from 'react';
import StepShell from './StepShell';
import FieldStack from './FieldStack';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Home, Building2, Info } from 'lucide-react';

const NumberStepper = ({ value, onChange, min = 0, max = 7 }) => (
  <div className='flex items-center gap-2'>
    <button
      type='button'
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={value <= min}
      className='w-10 h-10 rounded-lg border border-input bg-transparent hover:bg-muted text-lg font-semibold disabled:opacity-40 disabled:hover:bg-transparent'>
      −
    </button>
    <div className='flex-1 h-10 rounded-lg border border-input bg-background flex items-center justify-center font-display text-2xl tabular-nums'>
      {value}
    </div>
    <button
      type='button'
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={value >= max}
      className='w-10 h-10 rounded-lg border border-input bg-transparent hover:bg-muted text-lg font-semibold disabled:opacity-40 disabled:hover:bg-transparent'>
      +
    </button>
  </div>
);

/**
 * Interactive "typical week" strip.
 *
 * Each tile represents one working day. Tapping a tile toggles it between
 * home and office, updating the overall home/office counts. The strip is
 * both an input and a summary — users who prefer to "draw" their pattern
 * by tapping never need to touch the numeric stepper above, and vice
 * versa.
 *
 * The ordering is purely visual (home tiles default to the left) and is
 * reset to the canonical order whenever the numeric stepper is used; the
 * survey only captures the count of home/office days, not which specific
 * days (Mon/Tue/etc.), so any re-shuffle is lossless.
 */
const WeekPattern = ({ week, onToggle }) => {
  const wfh = week.filter((d) => d === 'home').length;
  const office = week.length - wfh;
  return (
    <div className='pt-1'>
      <div className='flex items-center gap-1.5'>
        {week.map((kind, i) => (
          <button
            key={i}
            type='button'
            onClick={() => onToggle(i)}
            aria-label={`Day ${i + 1} is ${kind}. Tap to switch to ${kind === 'home' ? 'office' : 'home'}.`}
            className={`flex-1 h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 border transition-all active:scale-[0.96] cursor-pointer ${
              kind === 'home'
                ? 'bg-primary/15 text-accent border-primary/30 hover:bg-primary/20'
                : 'bg-muted text-muted-foreground border-border hover:bg-muted/80 hover:border-muted-foreground/40'
            }`}>
            {kind === 'home' ? (
              <Home size={15} strokeWidth={1.75} />
            ) : (
              <Building2 size={15} strokeWidth={1.75} />
            )}
            <span className='text-[9px] font-mono uppercase tracking-[0.1em]'>
              {kind === 'home' ? 'home' : 'office'}
            </span>
          </button>
        ))}
      </div>
      <div className='flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground font-mono uppercase tracking-[0.12em]'>
        <span>Tap a day to toggle</span>
        <span>
          {wfh} home · {office} office
        </span>
      </div>
    </div>
  );
};

const WorkingPattern = () => {
  const [week, setWeek] = useState(() => ['home', 'home', 'home', 'office', 'office']);

  const daysPerWeek = week.length;
  const wfhDays = week.filter((d) => d === 'home').length;
  const officeDays = daysPerWeek - wfhDays;
  const mode = wfhDays === 0 ? 'office' : wfhDays === daysPerWeek ? 'home' : 'hybrid';

  const setDaysPerWeek = (n) => {
    setWeek((prev) => {
      if (n === prev.length) return prev;
      if (n < prev.length) return prev.slice(0, n);
      return [...prev, ...Array(n - prev.length).fill('office')];
    });
  };

  // Setting wfhDays via the stepper resets the strip to canonical order
  // (home tiles on the left). Any ad-hoc pattern the user built by tapping
  // tiles is not persisted by the survey anyway — we only submit counts.
  const setWfhDays = (n) => {
    setWeek((prev) => Array.from({ length: prev.length }, (_, i) => (i < n ? 'home' : 'office')));
  };

  const toggleDay = (i) => {
    setWeek((prev) => prev.map((d, idx) => (idx === i ? (d === 'home' ? 'office' : 'home') : d)));
  };

  return (
    <StepShell
      title='Your working pattern'
      description='Tell us about a typical week in the reporting period.'>
      <FieldStack label='How many days per week do you typically work?' required>
        <NumberStepper value={daysPerWeek} onChange={setDaysPerWeek} min={1} max={7} />
      </FieldStack>

      <FieldStack
        label='How many of those days do you work from home?'
        hint={
          daysPerWeek === 1
            ? '0 for office, 1 for home. Or tap the day below to toggle.'
            : `0 = always in the office, ${daysPerWeek} = always from home. Or tap the days below to build your pattern.`
        }
        required>
        <NumberStepper value={wfhDays} onChange={setWfhDays} min={0} max={daysPerWeek} />
      </FieldStack>

      <WeekPattern week={week} onToggle={toggleDay} />

      {mode === 'office' && (
        <Alert>
          <Info size={14} strokeWidth={1.5} />
          <AlertTitle>Always in the office</AlertTitle>
          <AlertDescription>
            We'll skip the home-office sections and go straight to your commute.
          </AlertDescription>
        </Alert>
      )}

      {mode === 'home' && (
        <Alert>
          <Info size={14} strokeWidth={1.5} />
          <AlertTitle>Fully remote</AlertTitle>
          <AlertDescription>
            We'll ask about your home setup and skip the commute section — no office travel to report.
          </AlertDescription>
        </Alert>
      )}

      {mode === 'hybrid' && (
        <Alert>
          <Info size={14} strokeWidth={1.5} />
          <AlertTitle>Hybrid pattern</AlertTitle>
          <AlertDescription>
            {wfhDays} day{wfhDays === 1 ? '' : 's'} from home and {officeDays} day
            {officeDays === 1 ? '' : 's'} in the office. We'll ask about both your home setup and your commute next.
          </AlertDescription>
        </Alert>
      )}
    </StepShell>
  );
};

export default WorkingPattern;
